import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import NotificationsBell from "@/components/NotificationsBell";
import {
  House,
  ChartLineUp,
  UsersThree,
  ShieldCheck,
  SignOut,
  ListChecks,
  EnvelopeSimple,
  Buildings,
  ArrowsLeftRight,
  ClipboardText,
  DotsThree,
  X,
} from "@phosphor-icons/react";

const MAX_BOTTOM_TABS = 5;

export default function Layout({ children }) {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  if (!user) return null;

  const tabs = navForRole(user.role);
  const hasMore = tabs.length > MAX_BOTTOM_TABS;
  const visibleTabs = hasMore ? tabs.slice(0, MAX_BOTTOM_TABS - 1) : tabs;
  const moreTabs = hasMore ? tabs.slice(MAX_BOTTOM_TABS - 1) : [];
  const moreActive = moreTabs.some((t) => loc.pathname.startsWith(t.to));

  const stopImpersonating = async () => {
    const orig = localStorage.getItem("tcd_token_orig");
    if (!orig) return;
    localStorage.setItem("tcd_token", orig);
    localStorage.removeItem("tcd_token_orig");
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      nav("/super/clubs");
    } catch (e) {
      await logout();
      nav("/login");
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      {user.impersonated_by && (
        <div className="bg-[#e3735e] text-black px-4 py-2 text-sm font-semibold flex items-center justify-between" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <ArrowsLeftRight size={16} weight="bold" />
            Impersonating <span className="font-bold">{user.name}</span> ({roleLabel(user.role)})
          </div>
          <button onClick={stopImpersonating} className="bg-black text-[#e3735e] px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider hover:bg-zinc-900" data-testid="stop-impersonate-btn">
            Stop & return
          </button>
        </div>
      )}

      <header className="sticky top-0 z-30 bg-[#09090b]/95 backdrop-blur border-b border-[#27272a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="w-7 h-7 rounded-md bg-[#dfff00] flex items-center justify-center font-heading text-black text-base">
              C
            </div>
            <div className="font-heading uppercase tracking-tight text-lg leading-none flex items-baseline">
              <span className="text-white">courtside</span><span className="text-[#dfff00] ml-0.5">OS</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-zinc-400" data-testid="user-name">{user.name}</span>
              <span className="role-badge" data-testid="user-role">{roleLabel(user)}</span>
            </div>
            <NotificationsBell />
            <button
              className="btn-ghost flex items-center gap-1.5"
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              data-testid="logout-btn"
            >
              <SignOut size={18} weight="bold" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <nav className="hidden md:block border-t border-[#27272a]">
          <div className="max-w-5xl mx-auto px-6 flex gap-1 overflow-x-auto no-scrollbar">
            {tabs.map((t) => {
              const active = loc.pathname.startsWith(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-3 text-sm font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                    active ? "border-[#dfff00] text-white" : "border-transparent text-zinc-500 hover:text-white"
                  }`}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-5 pb-28 md:pb-10" data-testid="main-content">
        {children}
      </main>

      {/* Mobile "More" sheet — gives access to every tab that doesn't fit in the bottom bar */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setMoreOpen(false)} data-testid="more-sheet-backdrop">
          <div
            className="absolute bottom-16 inset-x-0 bg-[#121212] border-t border-[#27272a] rounded-t-xl p-4 pb-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="more-sheet"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-label">More</span>
              <button onClick={() => setMoreOpen(false)} className="btn-ghost" data-testid="more-sheet-close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {moreTabs.map((t) => {
                const active = loc.pathname.startsWith(t.to);
                const Icon = t.icon;
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`more-tab-${t.id}`}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-md border ${
                      active ? "border-[#dfff00]/50 text-[#dfff00] bg-[#dfff00]/5" : "border-[#27272a] text-zinc-400"
                    }`}
                  >
                    <Icon size={22} weight={active ? "fill" : "regular"} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">{t.short}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-[#09090b] border-t border-[#27272a] bottom-nav z-30" data-testid="bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${visibleTabs.length + (hasMore ? 1 : 0)}, 1fr)` }}>
          {visibleTabs.map((t) => {
            const active = loc.pathname.startsWith(t.to);
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`bottom-tab-${t.id}`}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 ${active ? "text-[#dfff00]" : "text-zinc-500"}`}
              >
                <Icon size={22} weight={active ? "fill" : "regular"} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">{t.short}</span>
              </Link>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setMoreOpen((o) => !o)}
              data-testid="bottom-tab-more"
              className={`flex flex-col items-center justify-center gap-1 py-2.5 ${moreActive || moreOpen ? "text-[#dfff00]" : "text-zinc-500"}`}
            >
              <DotsThree size={22} weight="bold" />
              <span className="text-[10px] font-semibold uppercase tracking-wider">More</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}

function navForRole(role) {
  if (role === "super_admin")
    return [
      { id: "clubs", to: "/super/clubs", label: "Clubs", short: "Clubs", icon: Buildings },
    ];
  if (role === "head_coach")
    return [
      { id: "overview", to: "/overview", label: "Overview", short: "Home", icon: House },
      { id: "players", to: "/players", label: "Players", short: "Players", icon: UsersThree },
      { id: "evals", to: "/club/evaluations", label: "Evals", short: "Evals", icon: ClipboardText },
      { id: "invites", to: "/club/invites", label: "Invites", short: "Invite", icon: EnvelopeSimple },
      { id: "squads", to: "/club/squads", label: "Squads", short: "Squads", icon: ListChecks },
      { id: "users", to: "/club/users", label: "Users", short: "Users", icon: UsersThree },
      { id: "audit", to: "/club/audit", label: "Audit", short: "Audit", icon: ShieldCheck },
    ];
  if (role === "parent")
    return [
      { id: "parent", to: "/parent", label: "Progress", short: "Home", icon: ChartLineUp },
    ];
  return [
    { id: "players", to: "/players", label: "Players", short: "Players", icon: UsersThree },
  ];
}
